"use client";

import React, { useCallback, useMemo } from "react";
import { useCardChart } from "modules/cards/hooks/useCardChart";
import { CardSets, GameId } from "modules/common/model/models";
import { FinishEnum, GameCard } from "modules/cards/model/card";
import {
  EnhancedCardSet,
  EventMarker,
  SetCardAdapter,
  SetsByDateMap,
  TimelineDataPoint,
  TimelineSeriesConfig,
} from "modules/cards/components/RosenCardPriceChartTypes";
import { RosenTimelineChart } from "modules/common/components/charts/RosenTimelineChart";
import { ChartContainer } from "modules/cards/components/ChartContainer";
import { useCardFinishState } from "modules/cards/hooks/useCardFinishState";
import { pricingSourceForMarket } from "modules/common/pricing";
import { finishLabel } from "modules/cards/model/finishBehavior";

const baseSeries: TimelineSeriesConfig[] = [
  {
    name: "Trend",
    key: "value",
    color: "bg-primary",
    className: "stroke-primary",
    visible: true,
  },
  {
    name: "Low",
    key: "low",
    color: "bg-error",
    className: "stroke-error",
    visible: true,
  },
  {
    name: "MA7",
    key: "ma7",
    color: "bg-secondary",
    className: "stroke-secondary",
    visible: false,
  },
  {
    name: "MA14",
    key: "ma14",
    color: "bg-success",
    className: "stroke-success",
    visible: false,
  },
  {
    name: "MA30",
    key: "ma30",
    color: "bg-warning",
    className: "stroke-warning",
    visible: false,
  },
];

export interface CardPriceChartProps {
  card: GameCard;
  availableCharts: FinishEnum[];
  sets: CardSets;
}

// Helper to check if two dates are the same day
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
export function RosenCardPriceChart({
  card,
  availableCharts,
  sets,
}: CardPriceChartProps) {
  // Use shared finish state hook for automatic synchronization
  const { selectedFinish, apiFinish, setFinish } = useCardFinishState({ card });

  // Use the hook with shared state (React Query will cache data per finish).
  // The wrapper response includes the chart for the active finish under
  // `.chart` plus an `availableFinishes` list that supersedes any prop —
  // toggling can flip into a finish whose data we hadn't prefetched.
  const { chartData } = useCardChart(card, apiFinish);
  const chart = chartData.data?.chart ?? null;
  const chartMarket = chart?.market;
  const availableFinishes =
    chartData.data?.availableFinishes ?? availableCharts;
  const series = useMemo(
    () =>
      baseSeries.map((item) =>
        item.key === "value"
          ? {
              ...item,
              name: chartMarket
                ? (pricingSourceForMarket(chartMarket)
                    ?.primaryChartSeriesLabel ?? "Price")
                : "Price",
            }
          : item
      ),
    [chartMarket]
  );

  // Handle finish changes using shared state
  const handleFinishChange = useCallback(
    (finish: FinishEnum) => {
      setFinish(finish);
    },
    [setFinish]
  );

  // Derived event markers - process these first because we need them for timeline data
  const eventMarkers: EventMarker[] = useMemo(() => {
    if (!sets || !chart?.timestamps) {
      return [];
    }

    // Get date range from chart data
    const startDate = new Date(chart.timestamps[0]);
    const endDate = new Date(chart.timestamps[chart.timestamps.length - 1]);

    // Step 1: Get all sets within the date range
    const releasesInRange: EnhancedCardSet[] = Object.values(
      sets[card.game] ?? {}
    )
      .filter((cardSet) => {
        const releaseDate = new Date(cardSet.releaseDate);
        return (
          releaseDate >= startDate &&
          releaseDate <= endDate
        );
      })
      .map((set) => ({
        ...set,
        date: new Date(set.releaseDate),
        isMainSet:
          set.type === "core" ||
          set.type === "expansion" ||
          set.type === "promo" ||
          set.type === "spellbook" ||
          set.type === "draft_innovation",
      }));

    // Step 2: Group sets by release date
    const setsByDate: SetsByDateMap = releasesInRange.reduce(
      (acc: SetsByDateMap, set) => {
        const dateKey = set.releaseDate?.toString().split("T")[0];
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(set);
        return acc;
      },
      {}
    );

    // Step 3: For each date, select a set with priority order
    return Object.values(setsByDate).map((setsOnDate) => {
      // Prioritize by set type
      const coreSet = setsOnDate.find((set) => set.type === "core");
      const expansionSet = setsOnDate.find((set) => set.type === "expansion");
      const mastersSet = setsOnDate.find((set) => set.type === "masters");

      // Select the highest priority set available
      const selectedSet: EnhancedCardSet =
        coreSet || expansionSet || mastersSet || setsOnDate[0];

      // Create a GameCard-like object that SetTooltip can work with
      const setCard: SetCardAdapter = {
        setCode: selectedSet.code,
        set: {
          name: selectedSet.name,
          code: selectedSet.code,
        },
      };

      return {
        date: selectedSet.date,
        label: selectedSet.name,
        symbol: "triangle" as const,
        game: selectedSet.game as GameId,
        color: "#CAFF8A",
        setData: setCard,
        rawSetData: selectedSet,
        isMainSet:
          selectedSet.type === "core" ||
          selectedSet.type === "expansion" ||
          selectedSet.type === "promo" ||
          selectedSet.type === "spellbook" ||
          selectedSet.type === "draft_innovation",
      };
    });
  }, [chart?.timestamps, sets, card.game]);

  // Derived timeline data with set names
  const timelineData: TimelineDataPoint[] = useMemo(() => {
    if (!chart?.values || !chart?.timestamps) {
      return [];
    }

    // First, create the base timeline data
    const lowValues = chart.lowValues;
    const ma = chart.averages?.ma;
    const ma7 = ma?.["7d"];
    const ma14 = ma?.["14d"];
    const ma30 = ma?.["30d"];

    const baseData = chart.timestamps.map((timestamp: string, idx: number) => ({
      date: new Date(timestamp),
      value: Number(chart.values[idx].toFixed(2)),
      ...(lowValues?.[idx] ? { low: Number(lowValues[idx].toFixed(2)) } : {}),
      ...(ma7?.[idx] != null ? { ma7: Number(ma7[idx].toFixed(2)) } : {}),
      ...(ma14?.[idx] != null ? { ma14: Number(ma14[idx].toFixed(2)) } : {}),
      ...(ma30?.[idx] != null ? { ma30: Number(ma30[idx].toFixed(2)) } : {}),
    }));

    // Then, add set names to data points that match set release dates
    return baseData.map((dataPoint) => {
      // Find any event marker (set release) that occurs on the same day
      const relatedMarker = eventMarkers.find((marker) =>
        isSameDay(marker.date, dataPoint.date)
      );

      // If we found a matching marker, add the set name to the data point
      if (relatedMarker) {
        return {
          ...dataPoint,
          setName: relatedMarker.label,
        };
      }

      return dataPoint;
    });
  }, [chart, eventMarkers]);

  // Prepare chart controls
  const renderChartControls = useMemo(() => {
    // Filter to only show available finishes for this card
    const filteredFinishes = availableFinishes.filter((finish) =>
      card.finishes.includes(finish)
    );

    return (
      <div className="flex flex-wrap justify-center gap-1.5 md:justify-end md:gap-2">
        {filteredFinishes.map((finish, idx) => (
          <div key={idx} className="flex items-center">
            <label className="label cursor-pointer gap-1.5 py-0.5">
              <input
                type="radio"
                name="finish"
                className="radio radio-xs radio-primary md:radio-sm"
                checked={finish === selectedFinish}
                onChange={() => handleFinishChange(finish)}
              />
              <span className="label-text text-base-content/70 whitespace-nowrap text-xs md:text-sm">
                {finishLabel(finish)}
              </span>
            </label>
          </div>
        ))}
      </div>
    );
  }, [availableFinishes, card.finishes, selectedFinish, handleFinishChange]);

  // Prepare chart attribution
  const renderAttribution = useMemo(() => <></>, []);

  // Calculate initial time range to show 1 week of data
  const initialTimeRange = useMemo(() => {
    // Validate that we have data
    if (!timelineData.length) {
      return { start: 90, end: 100 };
    }

    // Verify data has valid dates
    const validData = timelineData.filter(
      (d) => d.date instanceof Date && !isNaN(d.date.getTime())
    );
    if (validData.length === 0) {
      return { start: 90, end: 100 };
    }

    // Get the date range from the data
    const dates = validData.map((d) => d.date.getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);

    // Handle edge case: all data on same date
    if (minDate === maxDate) {
      return { start: 0, end: 100 };
    }

    const totalRangeMs = maxDate - minDate;

    // Calculate 3 months in milliseconds
    const threeMonthsMs = 90 * 24 * 60 * 60 * 1000;

    // Calculate what percentage 3 months represents
    const threeMonthsPercentage = (threeMonthsMs / totalRangeMs) * 100;

    // Set the initial range to show the last 3 months
    const start = Math.max(0, Math.min(100, 100 - threeMonthsPercentage));

    return { start, end: 100 };
  }, [timelineData]);

  return (
    <ChartContainer
      title="Price Timeline"
      controls={renderChartControls}
      attribution={renderAttribution}
    >
      {chartData.isFetching ? (
        <div className="flex h-[340px] w-full items-center justify-center md:h-[380px]">
          <span className="loading loading-md loading-spinner text-primary"></span>
        </div>
      ) : chartData.isError ? (
        <div className="text-base-content/70 flex h-[340px] w-full items-center justify-center text-center text-sm md:h-[380px]">
          Couldn&apos;t load price history right now. Please try again later.
        </div>
      ) : chart === null ? (
        <div className="text-base-content/70 flex h-[340px] w-full items-center justify-center text-center text-sm md:h-[380px]">
          No price history available for this finish.
        </div>
      ) : (
        <RosenTimelineChart
          data={timelineData}
          series={series}
          eventMarkers={eventMarkers}
          currency={chart.currency}
          yAxisLabel={chart?.currency === "USD" ? "$" : "€"}
          className="w-full"
          initialTimeRange={initialTimeRange}
          initialActiveTimeframe="3M"
        />
      )}
    </ChartContainer>
  );
}
